import { useEffect, useState } from "react";
import { api, formatDate, formatDateTime } from "../lib/api";
import { StageBadge } from "../components/StageBadge";
import { ArrowUpRight, Calendar, Warning, UserPlus, ChartLineUp } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { FunnelWidget } from "../components/FunnelWidget";

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [recent, setRecent] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/kpis"),
      api.get("/dashboard/recent"),
      api.get("/followups/upcoming"),
    ]).then(([k, r, u]) => {
      setKpis(k.data);
      setRecent(r.data);
      setUpcoming(u.data);
    });
  }, []);

  if (!kpis) return <div className="p-8 font-mono text-xs uppercase tracking-wider text-[#5C5E66]">LOADING...</div>;

  return (
    <div className="p-8 max-w-[1400px]" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-1">Overview</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Dashboard</h1>
        </div>
        <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">{new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E2E2E5] border border-[#E2E2E5] mb-10" data-testid="kpi-grid">
        <KPI label="Total Physicians" value={kpis.total_physicians} icon={UserPlus} />
        <KPI label="Active Pipeline" value={kpis.active_pipeline} icon={ChartLineUp} />
        <KPI label="Upcoming (7d)" value={kpis.upcoming_followups} icon={Calendar} accent="info" />
        <KPI label="Overdue" value={kpis.overdue_followups} icon={Warning} accent={kpis.overdue_followups > 0 ? "error" : null} />
      </div>

      {/* Stage Distribution */}
      <div className="mb-10">
        <FunnelWidget />
      </div>

      <div className="mb-10">
        <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-3">Pipeline Distribution</div>
        <div className="bg-white border border-[#E2E2E5] p-5">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-px bg-[#E2E2E5] border border-[#E2E2E5]">
            {Object.entries(kpis.by_stage).map(([stage, count]) => (
              <div
                key={stage}
                className="bg-white p-3"
                data-testid={`stage-stat-${stage.toLowerCase().replace(/\s+/g,"-")}`}
              >
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#5C5E66] truncate" title={stage}>{stage}</div>
                <div className="font-mono font-semibold text-2xl mt-1 tabular-nums">{count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Physicians */}
        <div>
          <div className="flex items-end justify-between mb-3">
            <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Recent Leads</div>
            <Link to="/leads" className="text-xs font-mono uppercase tracking-wider text-[#006CFF] hover:text-[#0052CC] flex items-center gap-1">View All <ArrowUpRight size={12} weight="bold" /></Link>
          </div>
          <div className="bg-white border border-[#E2E2E5]">
            {recent.length === 0 ? (
              <div className="p-8 text-center text-[#5C5E66] text-sm">No leads yet. Add your first physician.</div>
            ) : recent.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-3 border-b border-[#E2E2E5] last:border-0 row-hover">
                <div className="w-9 h-9 bg-[#0A0B0F] text-white flex items-center justify-center font-mono font-semibold text-xs flex-shrink-0">
                  {p.full_name.split(/[\s,]+/).slice(0,2).map(s => s[0]).join("")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{p.full_name}</div>
                  <div className="text-xs text-[#5C5E66] truncate">{p.specialty || "—"} · {p.city || "—"}, {p.state || "—"}</div>
                </div>
                <StageBadge stage={p.stage} />
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Follow-ups */}
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-3">Upcoming Follow-ups</div>
          <div className="bg-white border border-[#E2E2E5]">
            {upcoming.length === 0 ? (
              <div className="p-8 text-center text-[#5C5E66] text-sm">All caught up.</div>
            ) : upcoming.slice(0, 8).map((fu) => {
              const overdue = new Date(fu.scheduled_date) < new Date();
              return (
                <div key={fu.id} className="p-3 border-b border-[#E2E2E5] last:border-0 row-hover" data-testid={`upcoming-fu-${fu.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">{fu.physician_name || "—"}</div>
                      <div className="text-xs text-[#5C5E66] mt-0.5">{fu.label}</div>
                    </div>
                    <div className={`text-[11px] font-mono ${overdue ? "text-[#E65100]" : "text-[#006CFF]"}`}>
                      {overdue ? "OVERDUE · " : ""}{formatDate(fu.scheduled_date)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

const KPI = ({ label, value, icon: Icon, accent }) => {
  const colorClass = accent === "error" ? "text-[#C62828]" : accent === "info" ? "text-[#1565C0]" : "text-[#0A0B0F]";
  return (
    <div className="bg-white p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-wider text-[#5C5E66]">{label}</div>
        <Icon size={16} weight="regular" className="text-[#5C5E66]" />
      </div>
      <div className={`font-mono font-semibold text-4xl mt-3 tabular-nums tracking-tight ${colorClass}`}>{value}</div>
    </div>
  );
};
